import React from 'react'

import {
  BET_ITEM_OUTCOME_REPLACE,
  BET_ITEM_STATE_UPDATE,
  BETSLIP_STATE_UPDATE,
  BETSLIP_UPDATE_BET,
  EVENT_STATE_UPDATE
} from '../../actions'
import SportManager from '../../managers/sport'
import RacingManager from '../../managers/racing'
import SubscriptionManager from '../../connection/subscription'
import { useBetslipDispatch, useBetslipState } from './betslip.context'
import { isArray, isEmpty, isEqual, merge } from 'lodash'
import {
  getBetEventPartials,
  getBonusAssignedToBet,
  getIntUID,
  isBetActive,
  isBetSuspended,
  isFeedOffline,
  isMultilegsMarket,
  logger
} from '../../utils'
// import { useParamChange } from './useParamChange'
import { useApplicationState } from '../root'
import { BET_EVENT_TYPE, BONUS_TYPE, RaceLogicMarkets } from '../../constants'
import { BETSLIP_ERROR_CODE } from '../../error'
import Bus from '../../bus'
import { useObservableOddsContext } from '../../ui/odds.observer'
import { animateOddsInFixedTime } from '../../utils/animation'
import { useSettings } from '../settings'

const BetItemContext = React.createContext()

export function BetItemProvider (props) {
  const {
    children,
    betItem
  } = props

  const betItemState = createBetItemState(betItem)

  return (
    <BetItemContext.Provider value={betItemState}>
      {children}
    </BetItemContext.Provider>
  )
}

export function useBetItem () {
  return React.useContext(BetItemContext)
}

export function useBetAmountChangeHandler () {
  const { outcomeId, eventId, requestId } = useBetItem()
  const dispatch = useBetslipDispatch()

  return React.useCallback(
    (amount, freeBetId) => dispatch({
      type: BETSLIP_UPDATE_BET,
      payload: {
        requestId,
        eventId,
        outcomeId,
        amount,
        freeBetId
      }
    }),
    [outcomeId]
  )
}

function applyBetEventStateUpdate (state, eventUpdate) {
  const event = merge(state.event, eventUpdate)
  const updatePayload = getBetEventPartials(
    event,
    {
      marketTypeId: state.marketTypeId,
      marketId: state.marketId,
      outcomeId: state.outcomeId,
      betEventType: state.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH
    }
  )

  return {
    event,
    ...updatePayload
  }
}

function betItemReducer (state, action) {
  // Event state diff update
  if (action.type === EVENT_STATE_UPDATE) {
    const update = applyBetEventStateUpdate(state, action.payload)

    return { ...state, ...update }
  }

  // Client-side update
  if (action.type === BET_ITEM_STATE_UPDATE) {
    return { ...state, ...action.payload }
  }

  if (action.type === BET_ITEM_OUTCOME_REPLACE) {
    const {
      marketTypeId,
      marketId,
      outcomeId,
      betEventType
    } = action.payload

    // Extracting event partials using new ids
    const partialsUpdate = getBetEventPartials(
      state.event,
      {
        marketTypeId,
        marketId,
        outcomeId,
        betEventType: betEventType || BET_EVENT_TYPE.SPORT_PREMATCH
      }
    )

    const updatedState = {
      ...state,
      ...action.payload,
      ...partialsUpdate
    }

    // Recalculating statuses for replaced bet
    updatedState.active = isBetActive(updatedState)
    updatedState.suspended = isBetSuspended(updatedState)

    return updatedState
  }

  return state
}

const createBetItemInitialState = betItem => {
  /*
    Right now generating ids in bet item state is not working
    because of BETSLIP_UPDATE_BET action working with requestId for updating
  */
  const requestId = betItem.requestId || getIntUID()

  // Unsynchronized or without provided event data bet
  if (!betItem.synchronized || isEmpty(betItem.event)) {
    return {
      ...betItem,
      requestId,
      loading: true,
      suspended: false,
      active: false,
      errors: undefined,
      event: {},
      marketType: {},
      market: {},
      outcome: {},
      runner: {},
      odds: undefined,
      oddsForMulti: undefined,
      oddsToDisplay: undefined
    }
  }

  const betEventPartials = getBetEventPartials(
    betItem.event,
    {
      marketTypeId: betItem.marketTypeId,
      marketId: betItem.marketId,
      outcomeId: betItem.outcomeId,
      betEventType: betItem.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH
    }
  )

  return {
    ...betItem,
    requestId,
    loading: betItem.loading || false,
    errors: null,
    // Data partials
    event: betItem.event,
    ...betEventPartials
  }
}

function createBetItemState (betItem) {
  const [state, dispatch] = React.useReducer(
    betItemReducer,
    betItem,
    createBetItemInitialState
  )

  const assignedBonusRef = React.useRef(null)
  const { connection } = useApplicationState()
  const betslipDispatch = useBetslipDispatch()
  const settings = useSettings()

  // Auto applying bet event partials
  React.useEffect(
    () => {
      logger.debug(
        `[betitem:${state.requestId}]`,
        'replacing bet outcome',
        'triggered external outcome id change',
        betItem.outcomeId
      )

      dispatch({
        type: BET_ITEM_OUTCOME_REPLACE,
        payload: {
          marketTypeId: betItem.marketTypeId,
          marketId: betItem.marketId,
          outcomeId: betItem.outcomeId,
          betEventType: betItem.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH
        }
      })
    },
    [betItem.outcomeId]
  )

  // Throws up outcome odds with request id
  // Broadcasting counted status to betslip
  React.useEffect(
    () => {
      const payload = {
        betEventType: state.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
        eventId: state.eventId,
        marketTypeId: state.marketTypeId,
        marketId: state.marketId,
        outcomeId: state.outcomeId,
        odds: state.odds,
        oddsForMulti: state.oddsForMulti,
        requestId: state.requestId,
        active: state.active,
        suspended: state.suspended || state.feedOffline,
        synchronized: true,
        eventSource: state.event.source, // event provider name
        providerName: state.marketType?.providerName, // market provider name

        // Racing Only
        runner: state.runner,

        // Additionaly providing onlySingle options
        // FIXME: Temp solution
        onlySingle: state.event.onlySingle
      }

      logger.debug(
        `[betitem:${state.requestId}]`,
        'broadcasting bet to betslip',
        payload
      )

      betslipDispatch({
        type: BETSLIP_UPDATE_BET,
        payload
      })
    },
    [
      state.odds,
      state.oddsForMulti,
      state.active,
      state.suspended,
      state.feedOffline,
      state.outcomeId
    ]
  )

  React.useEffect(
    () => {
      let eventResolveDeffer

      if (isEmpty(state.event) || !state.synchronized) {
        // Starting async resolve and broadcasting loading
        dispatch({
          type: BET_ITEM_STATE_UPDATE,
          payload: {
            loading: true
          }
        })

        switch (state.betEventType) {
          case BET_EVENT_TYPE.RACING:
            eventResolveDeffer = RacingManager.getRaceById(state.eventId).then(data => data.race)
            break

          case BET_EVENT_TYPE.SPORT_PREMATCH:
          case BET_EVENT_TYPE.SPORT_LIVE:
          default:
            eventResolveDeffer = SportManager.findEvent(state.eventId)
            break
        }
      } else {
        eventResolveDeffer = Promise.resolve(state.event)
      }

      eventResolveDeffer
        .then(event => {
          const updatePayload = getBetEventPartials(
            event,
            {
              marketTypeId: betItem.marketTypeId,
              marketId: betItem.marketId,
              outcomeId: betItem.outcomeId,
              betEventType: betItem.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH
            }
          )

          // Now limits are retrieved within the bet:validate request

          // if (betItem.betEventType === BET_EVENT_TYPE.SPORT_PREMATCH) {
          //   if (settings?.betslip?.loadOutcomeLimits) {
          //     BetManager.getOutcomeLimits(betItem.outcomeId)
          //       .then((limits) => {
          //         if (!isEmpty(limits)) {
          //           dispatch({
          //             type: BET_ITEM_STATE_UPDATE,
          //             payload: {
          //               ...limits
          //             }
          //           })
          //         }
          //       })
          //       .catch((error) => {
          //         console.log('getOutcomeLimits ERR: ', error)
          //       })
          //   }
          // }

          if (RaceLogicMarkets[betItem.marketTypeId] || isMultilegsMarket(betItem.marketTypeId)) {
            RacingManager.getOutcomeById(betItem.outcomeId)
              .then(({ outcome }) => {
                dispatch({
                  type: BET_ITEM_STATE_UPDATE,
                  payload: {
                    event,
                    loading: false,
                    feedOffline: isFeedOffline(feedStates[event.source]),
                    ...updatePayload,
                    outcome,
                    odds: outcome.odds,
                    oddsForMulti: outcome.oddsForMulti
                  }
                })
              })
              .catch((error) => {
                dispatch({
                  type: BET_ITEM_STATE_UPDATE,
                  payload: {
                    error,
                    active: false,
                    loading: false
                  }
                })
              })
          } else {
            dispatch({
              type: BET_ITEM_STATE_UPDATE,
              payload: {
                event,
                loading: false,
                feedOffline: isFeedOffline(feedStates[updatePayload.marketType?.providerName || event.source]),
                ...updatePayload
              }
            })
          }
        })
        .catch(error => {
          dispatch({
            type: BET_ITEM_STATE_UPDATE,
            payload: {
              error,
              active: false,
              loading: false
            }
          })
        })

      return () => {
        // Setting bet unsynchronized status
        // Cleaning bet requestId
        betslipDispatch({
          type: BETSLIP_UPDATE_BET,
          payload: {
            requestId: state.requestId,
            synchronized: false,
            event: {}
          }
        })

        // Updating own state
        dispatch({
          type: BET_ITEM_STATE_UPDATE,
          payload: {
            synchronized: false
          }
        })
      }
    },
    [connection.id]
  )

  /*
    Subscribing (to DataUpdatesBroadcaster) for event updates
  */
  React.useEffect(
    () => {
      // Subscription topics in tournament scope
      const tournamentSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.EXPERIMENTAL_EVENT,
          key: state.eventId
        }
      ]

      // Update handler
      const eventUpdateHandler = (action) => dispatch(action)
      const raceUpdateHandler = payload => {
        if (payload.data?.race?.raceId === state.eventId) {
          dispatch({
            type: EVENT_STATE_UPDATE,
            payload: payload.data.race
          })
        }
      }

      // Internal subscription in app
      SportManager.dataBroadcaster.subscribeToEventUpdates(state.eventId, eventUpdateHandler)

      // External subscription on Notifier Service
      SubscriptionManager.notifierSubscribe(tournamentSubscriptionTopics)

      Bus.on(Bus.events.racing.raceStatusUpdate, raceUpdateHandler)

      return () => {
        // External subscription on Notifier Service
        SubscriptionManager.notifierUnsubscribe(tournamentSubscriptionTopics)

        // Internal subscription in app
        SportManager.dataBroadcaster.unsubscribeFromEventUpdates(state.eventId, eventUpdateHandler)
        Bus.off(Bus.events.racing.raceStatusUpdate, raceUpdateHandler)
      }
    },
    []
  )

  const {
    errors,
    outcomesErrors,
    settings: { feedStates },
    bonusAssignments,
    bonuses,
    limits
  } = useBetslipState()

  const { hasUnacceptedOdds, odds: observableOdds } = useObservableOddsContext()

  React.useEffect(() => {
    // console.log(99, 'changed odds effect')
    const { requestId, outcomeId, odds, assignedBonus } = state
    if (hasUnacceptedOdds && observableOdds[outcomeId] !== odds) {
      // console.log(99, 'odds changed!', { odds, newOdds: observableOdds[outcomeId] })

      // remove odds boost bonus assignment if odds have changed
      // for PROMO_ODDS it's not needed because PROMO_ODDS are fixed final odds
      if (assignedBonus?.bonusType === BONUS_TYPE.ODDS_BOOST) {
        console.log('removing odds boost bonus assignment due to odds change', { assignedBonus })

        betslipDispatch({
          type: BETSLIP_STATE_UPDATE,
          payload: {
            bonusAssignments: (bonusAssignments || [])
              .filter(ba => !(ba.requestId === requestId && ba.bonusId === assignedBonus.id))
          }
        })
      }
    }
  }, [hasUnacceptedOdds, observableOdds, state.odds, state.requestId, state.outcomeId, state.assignedBonus])

  const isOddsBoostOrPromoOdds = bonus => [BONUS_TYPE.ODDS_BOOST, BONUS_TYPE.PROMO_ODDS].includes(bonus?.bonusType)

  // react to ODDS_BOOST bonus assignment change: set oddsToDisplay boosted or not
  React.useEffect(() => {
    const { odds, oddsToDisplay, assignedBonus } = state

    if (isOddsBoostOrPromoOdds(assignedBonus)) {
      const { boostedBetOdds } = assignedBonus.props

      if (odds !== assignedBonus.props.betOdds) {
        console.warn('bet odds and odds to boost are different: ', { odds, boostedBetOdds })
      }

      if (boostedBetOdds > odds && boostedBetOdds !== oddsToDisplay) {
        animateOddsInFixedTime({
          start: odds,
          end: boostedBetOdds,
          continueAnimation: () => isOddsBoostOrPromoOdds(assignedBonusRef.current),
          callback: value => {
            if (isOddsBoostOrPromoOdds(assignedBonusRef.current)) { // check if still enabled ODDS_BOOST
              dispatch({
                type: BET_ITEM_STATE_UPDATE,
                payload: {
                  oddsToDisplay: value
                }
              })
            } else { // if canceled ODDS_BOOST bonus during animation
              dispatch({
                type: BET_ITEM_STATE_UPDATE,
                payload: {
                  oddsToDisplay: odds
                }
              })
            }
          }
        })
      }
    } else {
      dispatch({
        type: BET_ITEM_STATE_UPDATE,
        payload: {
          oddsToDisplay: odds
        }
      })
    }
  }, [state.odds, state.requestId, state.assignedBonus])

  // set freebet amount if FREEBET bonus is assigned
  React.useEffect(() => {
    const { requestId, amount, assignedBonus } = state

    if (assignedBonus?.bonusType === BONUS_TYPE.FREEBET && amount !== assignedBonus.freeBetAmount) {
      betslipDispatch({
        type: BETSLIP_UPDATE_BET,
        payload: {
          requestId,
          amount: assignedBonus.freeBetAmount
        }
      })
    }
  }, [state.requestId, state.amount, state.assignedBonus])

  // Syncing betslip errors with own state
  React.useEffect(
    () => {
      const betError = isArray(errors) ? errors.find(error => error.requestId === state.requestId) : errors[state.requestId]

      if (!isEqual(betError?.violations, state.errors)) {
        const payload = betError
          ? { errors: betError.violations }
          : { errors: undefined }

        dispatch({
          type: BET_ITEM_STATE_UPDATE,
          payload: payload
        })
      }
    },
    [errors]
  )

  // Syncing betslip bonusAssignments with own state
  React.useEffect(
    () => {
      const assignedBonus = getBonusAssignedToBet({ requestId: state.requestId, bonusAssignments, bonuses })

      if (!isEqual(assignedBonus, state.assignedBonus)) {
        const payload = assignedBonus
          ? { assignedBonus }
          : { assignedBonus: null }

        assignedBonusRef.current = assignedBonus

        dispatch({
          type: BET_ITEM_STATE_UPDATE,
          payload: payload
        })
      }
    },
    [bonusAssignments, bonuses, state.requestId]
  )

  if (betItem.betEventType === BET_EVENT_TYPE.RACING || settings?.betslip?.loadOutcomeLimits) {
    // Syncing betslip limits with own state
    React.useEffect(() => {
      const betLimits = {
        ...(limits?.[state.requestId] || { maxBetAmount: null })
      }

      dispatch({
        type: BET_ITEM_STATE_UPDATE,
        payload: {
          ...betLimits
        }
      })
    }, [limits, state.requestId]);
  }

  React.useEffect(() => {
    const outcomeOddsChanged = outcomesErrors[state.outcomeId]
    if (outcomeOddsChanged) {
      const payload = {
        odds: outcomeOddsChanged.violationInfo.newOdds,
        oddsForMulti: outcomeOddsChanged.violationInfo.newOddsForMulti,
        outcome: { ...state.outcome, odds: outcomeOddsChanged.violationInfo.newOdds, oddsForMulti: outcomeOddsChanged.violationInfo.newOddsForMulti }
      }

      payload.active = outcomesErrors[state.outcomeId]?.code !== BETSLIP_ERROR_CODE.OUTCOME_INACTIVE
      payload.suspended = outcomesErrors[state.outcomeId]?.code === BETSLIP_ERROR_CODE.OUTCOME_INACTIVE

      dispatch({
        type: BET_ITEM_STATE_UPDATE,
        payload: payload
      })
    }
  }, [outcomesErrors])

  React.useEffect(
    () => {
      dispatch({
        type: BET_ITEM_STATE_UPDATE,
        payload: {
          feedOffline: isFeedOffline(feedStates[state.event.source])
        }
      })
    },
    [feedStates, state.event.source]
  )

  // useParamChange(state, dispatch)

  return state
}
